import Toast from 'components/ui/Toast.vue';
import CustomDialog from 'components/ui/CustomDialog.vue';
import ComponentDialog from 'components/ui/ComponentDialog.vue';
import ENV from 'config/env';

window.addEventListener('unhandledrejection', (event) => {
    // Prevent error output on the console:
    event.preventDefault();

    // Do not log to console for these environments.
    if (['production', 'staging'].includes(ENV.APP_ENV)) {
        return;
    }

    if (event?.reason?.name === 'JdsError' && event?.reason?.type === 'cancel') {
        return;
    }

    /* eslint-disable no-console */
    if (event.reason && event.reason.name) {
        console.group('Uncaught Rejection:', event.reason.name);
        if (event.reason.message) {
            console.warn(event.reason.message);
        }
        console.warn(event.reason.stack);
        return console.groupEnd();
    }

    console.warn('Uncaught Rejection:', event);
    /* eslint-enable no-console */
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const UPDATED_EVENT = 'updated';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MOUNTED_EVENT = 'mounted';

export class Notifier {
    constructor() {
        this.components = [];
        this.containers = [];
        this.vm = null;
        this.i18n = null;
        this.defaultContainerName = 'toasts';

        this.initContainers();

        this.modals = [];
    }

    /**
     * Initialises the containers to mount dynamic components.
     *
     * @memberOf Notifier
     */
    initContainers() {
        this.containers.toasts = this.createContainer(this.defaultContainerName);
        this.containers.modals = this.createContainer('modals');
    }

    /**
     * Create a container in DOM for mounting components
     *
     * @param {any} name
     * @returns
     *
     * @memberOf Notifier
     */
    createContainer(name) {
        const container = document.createElement('div');
        container.classList.add(name);
        document.body.appendChild(container);
        return container;
    }

    buildElement(type, config, containerName) {
        const element = document.createElement('div');
        let container = this.containers[type.toLowerCase()];

        // Create or use the specified container
        if (containerName) {
            container = this.containers[containerName] || this.createContainer(containerName);
            this.containers[containerName] = container;
        }

        container.appendChild(element);
        const newComponent = new this.components[type]({ propsData: config });
        if (!newComponent._i18n) {
            newComponent._i18n = this.i18n;
        }
        if (!newComponent._route && config && config.route) {
            newComponent._route = config.route;
        }

        newComponent.$mount(element);

        return newComponent;
    }

    /**
     * Show a toast
     *
     * Config will be passed to the new component constructor as
     * propData to initalise the props of the new toast component.
     *
     * Toasts will be mounted in different containers based on container name.
     * If the container doesn't exist a new DOM element is created and used
     * as the container. If no container name is provided then the
     * default container is used.
     *
     * @param {String} msg
     * @param {any} config
     * @param {String} containerName - The name of the container to mount the toast in.
     * @returns
     *
     * @memberOf Notifier
     */
    toast(msg, config, containerName = '') {
        if (config && config.xAlign && config.xAlign === 'center') {
            containerName = 'toast_center';
        }
        const newToast = this.buildElement('Toast', config, containerName || 'toasts');

        // Show the toast and once resolved destroy it and remove from DOM
        newToast.message = msg;
        return newToast.show().then(() => {
            this.destroy(newToast);
        });
    }

    /**
     * Show a dialog
     *
     * @param {any} config
     * @param {String} containerName - The name of the container to mount the toast in.
     * @returns
     *
     * @memberOf Notifier
     */
    dialog(config, containerName) {
        const newDialog = this.buildElement('CustomDialog', config, containerName || 'modals');

        // Show the toast and once resolved destroy it and remove from DOM
        return newDialog.show().then(
            (result) => {
                this.destroy(newDialog);
                return result;
            },
            (error) => {
                this.destroy(newDialog);
                throw error;
            },
        );
    }

    /**
     * Show a component in a modal with a custom component
     *
     * Components must be declared in the global Vue context
     *
     * @param {any} config
     * @param {String} containerName - The name of the container to mount the toast in.
     * @returns
     *
     * @memberOf Notifier
     */
    modal(config, containerName = '') {
        const modal = this.buildElement('ComponentDialog', config, containerName || 'modals');
        this.modals.push(modal);
        // Show the toast and once resolved destroy it and remove from DOM
        return modal.show();
    }

    /**
     * Show a confirm box
     *
     * Box launches with OK and CANCEL buttons
     * OK returns true
     * Cancel returns false
     *
     * @param {any} config - Confirm box config
     * @param {String} containerName - The name of the container to mount the toast in.
     * @returns {Promise}
     *
     * @memberOf Notifier
     */
    confirm(config, containerName = 'modals') {
        const options = config || {};
        options.withCancel = true;
        options.className = 'confirm-dialog';
        if (!options.buttons) {
            options.buttons = [
                {
                    label: this.i18n.t('common.proceed'),
                    value: true,
                    type: 'ui-secondary-button',
                    props: {
                        modal: true,
                    },
                },
            ];
        }
        return this.dialog(options, containerName);
    }

    /**
     * Show a Alert box
     *
     * Box launches with OK
     * OK returns true
     *
     * @param {String} message - Text to appear in the modal
     * @param {any} config - Confirm box config
     * @param {String} containerName - The name of the container to mount the toast in.
     * @returns {Promise}
     *
     * @memberOf Notifier
     */
    alert(message, config, containerName) {
        const options = config || {};
        options.className += ' alert-dialog';
        options.buttons = [
            {
                label: 'Ok',
                value: true,
                type: 'ui-primary-button',
                props: {
                    modal: true,
                },
            },
        ];

        options.message = options.message || message;
        return this.dialog(options, containerName);
    }

    /**
     * Destroys component logic and DOM elements for any thrown component
     *
     * Used to destroy toasts, confirms, dialogs and alerts internally
     * Externally it can be used to clean up a modal containing a custom component
     *
     * @param {any} modal - The modal to destroy
     *
     * @memberOf Notifier
     */
    destroy(modal) {
        modal.hide();
        this.modals.splice(this.modals.indexOf(modal), 1);
        if (modal && modal.$el.parentElement) {
            modal.$el.parentElement.removeChild(modal.$el);
        }
        modal.$destroy();
    }

    /**
     * Destroys all modals currently on screen
     *
     * @memberOf Notifier
     */
    destroyAllModals() {
        this.modals.forEach(modal => this.destroy(modal));
    }

    /**
     * Install method for Vue plugin
     *
     * @param {any} Vue
     *
     * @memberOf Notifier
     */
    install(Vue) {
        /* eslint-disable vue/multi-word-component-names */
        this.components.Toast = Vue.component('Toast', Toast);
        this.components.CustomDialog = Vue.component('CustomDialog', CustomDialog);
        this.components.ComponentDialog = Vue.component('ComponentDialog', ComponentDialog);
        /* eslint-enable vue/multi-word-component-names */

        Vue.prototype.$notify = this;
    }
}
